import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Calendar, MapPin, Clock, Users, Trophy, 
  ChevronRight, Ticket, Star, Check,
  ArrowRight
} from 'lucide-react';
import { Button, AnimatedBorder } from '../ui';
import { useLandingContent } from '../../hooks/useLandingContent';

const FALLBACK_UPCOMING_TRYOUTS = [
  {
    id: 'xi',
    title: 'XI Tryout Nacional BTalentStar',
    subtitle: 'Puerto Varas',
    location: 'Puerto Varas, Región de Los Lagos',
    venue: 'Cancha El Mirador',
    address: 'Camino a Alerce s/n (aproximadamente en el Km 2,5), Puerto Varas, Chile',
    date: '2026-10-24',
    dateKey: 'tryouts.displayDate' as const,
    taglineKey: 'tryouts.tagline' as const,
    introKey: 'tryouts.nextIntro' as const,
    spots: 0,
    price: 49990,
    currency: 'CLP',
    clubs: [
      { name: 'Universidad Católica', logo: '/images/tryouts/clubs-xi/universidad-catolica.png' },
      { name: 'Universidad de Chile', logo: '/images/tryouts/clubs-xi/universidad-de-chile.png' },
      { name: 'Audax Italiano', logo: '/images/tryouts/clubs-xi/audax-italiano.png' },
      { name: 'Huachipato FC', logo: '/images/tryouts/clubs-xi/huachipato.png' },
      { name: 'Unión Española', logo: '/images/tryouts/clubs-xi/union-espanola.png' },
    ],
    categories: [
      { nameKey: 'tryouts.catMorning' as const, accreditation: '08:30 hrs', event: '08:30 a 13:30 hrs' },
      { nameKey: 'tryouts.catAfternoon' as const, accreditation: '14:30 hrs', event: '14:30 a 19:30 hrs' },
    ],
    includeKeys: ['tryouts.include1', 'tryouts.include2', 'tryouts.include3', 'tryouts.include4', 'tryouts.include5'] as const,
    buyLink: 'https://www.ticketmaster.cl/event/xi-tryout-nacional-btalentstar-cancha-el-mirador-puerto-varas',
  },
  {
    id: 'xii',
    title: 'XII Tryout Nacional BTalentStar',
    subtitle: 'Pruebas Multiclub – Chicureo',
    location: 'Chicureo, Región Metropolitana',
    venue: 'Santa Esther Sport Chicureo',
    address: 'Camino Santa Esther Parcela 4, Colina / Chicureo',
    date: '2026-11-14',
    dateKey: 'tryouts.displayDateXii' as const,
    taglineKey: 'tryouts.taglineXii' as const,
    introKey: 'tryouts.nextIntroXii' as const,
    spots: 0,
    price: 49990,
    currency: 'CLP',
    clubs: [
      { name: 'Universidad Católica', logo: '/images/tryouts/clubs-xii/universidad-catolica.png' },
      { name: 'Universidad de Chile', logo: '/images/tryouts/clubs-xii/universidad-de-chile.png' },
      { name: 'Audax Italiano', logo: '/images/tryouts/clubs-xii/audax-italiano.png' },
      { name: 'Unión Española', logo: '/images/tryouts/clubs-xii/union-espanola.png' },
      { name: 'Huachipato FC', logo: '/images/tryouts/clubs-xii/huachipato.png' },
    ],
    categories: [
      { nameKey: 'tryouts.catMorning' as const, accreditation: '08:30 hrs', event: '08:30 a 13:30 hrs' },
      { nameKey: 'tryouts.catAfternoon' as const, accreditation: '14:30 hrs', event: '14:30 a 19:30 hrs' },
    ],
    includeKeys: ['tryouts.include1', 'tryouts.include2', 'tryouts.include3', 'tryouts.include4Meds', 'tryouts.include5'] as const,
    buyLink: 'https://www.ticketmaster.cl/event/xii-tryout-nacional-btalentstar-santa-esther-sport',
  },
];

// Ediciones anteriores con videos de YouTube - titleKey/descriptionKey resolved with t() at render
const FALLBACK_PAST_TRYOUTS = [
  { id: 10, titleKey: 'tryouts.past10Title' as const, descriptionKey: 'tryouts.past10Desc' as const, dateKey: 'tryouts.past10Date' as const, location: 'La Florida, Santiago, Chile', participants: 150, selected: 51, clubs: ['Universidad de Chile', 'Universidad Católica', 'Audax Italiano', 'Unión Española', 'Huachipato FC'], highlightKeys: ['tryouts.past10h1', 'tryouts.past10h2', 'tryouts.past10h3'] as const, videoId: 'nx5-6eglOng' },
  { id: 9, titleKey: 'tryouts.past9Title' as const, descriptionKey: 'tryouts.past9Desc' as const, dateKey: 'tryouts.past9Date' as const, location: 'Viña del Mar, Chile', participants: 252, selected: 0, clubs: ['Universidad Católica', 'Universidad de Chile', 'Audax Italiano', 'Unión Española', 'Huachipato FC'], highlightKeys: ['tryouts.past9h1', 'tryouts.past9h2', 'tryouts.past9h3'] as const, videoId: 'sfgZsP_xIJk' },
  { id: 8, titleKey: 'tryouts.past8Title' as const, descriptionKey: 'tryouts.past8Desc' as const, dateKey: 'tryouts.past8Date' as const, location: 'Santiago, Chile', participants: 200, selected: 0, clubs: ['Universidad Católica', 'Universidad de Chile', 'Unión Española', 'Deportes Recoleta'], highlightKeys: ['tryouts.past8h1', 'tryouts.past8h2', 'tryouts.past8h3'] as const, videoId: 'sfgZsP_xIJk' },
  { id: 7, titleKey: 'tryouts.past7Title' as const, descriptionKey: 'tryouts.past7Desc' as const, dateKey: 'tryouts.past7Date' as const, location: 'Máfil, Chile', participants: 100, selected: 0, clubs: ['Universidad de Chile', 'Universidad Católica'], highlightKeys: ['tryouts.past7h1', 'tryouts.past7h2'] as const, videoId: 'kk1xLr2Y4R8' },
  { id: 6, titleKey: 'tryouts.past6Title' as const, descriptionKey: 'tryouts.past6Desc' as const, dateKey: 'tryouts.past6Date' as const, location: 'Santiago, Chile', participants: 200, selected: 0, clubs: ['Argentinos Juniors', 'Universidad de Chile', 'Universidad Católica', 'Unión Española'], highlightKeys: ['tryouts.past6h1', 'tryouts.past6h2', 'tryouts.past6h3', 'tryouts.past6h4'] as const, videoId: 'ZlB9Pl_ZAbE' },
  { id: 5, titleKey: 'tryouts.past5Title' as const, descriptionKey: 'tryouts.past5Desc' as const, dateKey: 'tryouts.past5Date' as const, location: 'Valparaíso, Chile', participants: 150, selected: 17, clubs: ['Universidad Católica', 'Universidad de Chile', 'Everton', 'San Luis', 'Concón National'], highlightKeys: ['tryouts.past5h1', 'tryouts.past5h2'] as const, videoId: 'A7TpWl46Ewo' },
  { id: 4, titleKey: 'tryouts.past4Title' as const, descriptionKey: 'tryouts.past4Desc' as const, dateKey: 'tryouts.past4Date' as const, location: 'Santiago, Chile', participants: 100, selected: 31, clubs: ['Universidad de Chile', 'Universidad Católica', 'Deportes Recoleta'], highlightKeys: ['tryouts.past4h1', 'tryouts.past4h2', 'tryouts.past4h3'] as const, videoId: 'LMNIZ8iBGS8' },
  { id: 3, titleKey: 'tryouts.past3Title' as const, descriptionKey: 'tryouts.past3Desc' as const, dateKey: 'tryouts.past3Date' as const, location: 'Santiago, Chile', participants: 100, selected: 31, clubs: ['Universidad Católica', 'Barnechea', 'Recoleta', 'Universidad de Chile', 'Everton'], highlightKeys: ['tryouts.past3h1', 'tryouts.past3h2'] as const, videoId: 'lU6a8ocXTRI' },
  { id: 2, titleKey: 'tryouts.past2Title' as const, descriptionKey: 'tryouts.past2Desc' as const, dateKey: 'tryouts.past2Date' as const, location: 'Lima, Perú', participants: 100, selected: 47, clubs: ['Argentinos Juniors', 'Universidad Católica', 'Alianza Lima', 'Sports Boys', 'C.A. Mannucci', 'FPF'], highlightKeys: ['tryouts.past2h1', 'tryouts.past2h2', 'tryouts.past2h3'] as const, videoId: '_1AGV8QoU7k' },
  { id: 1, titleKey: 'tryouts.past1Title' as const, descriptionKey: 'tryouts.past1Desc' as const, dateKey: 'tryouts.past1Date' as const, location: 'Santiago, Chile', participants: 100, selected: 51, clubs: ['Argentinos Juniors', 'Defensa y Justicia', 'Universidad de Chile', 'Universidad Católica', 'Audax Italiano', 'A.C. Barnechea'], highlightKeys: ['tryouts.past1h1', 'tryouts.past1h2'] as const, videoId: '3COtIp5LESI' },
];

// Upcoming tryouts: prefer CMS when it has 2+; otherwise dual hardcoded fallbacks (XI + XII).
interface FeaturedTryout {
  key: string;
  title: string;
  subtitle: string;
  location: string;
  venue: string;
  address: string;
  dateDisplay: string;
  tagline: string;
  intro: string;
  spots: number;
  price: number;
  clubs: { name: string; logo: string }[];
  categories: { name: string; accreditation: string; event: string }[];
  includes: string[];
  buyLink: string;
}

interface PastTryout {
  key: string;
  title: string;
  dateDisplay: string;
  location: string;
  participants: number;
  selected: number;
  clubs: string[];
  highlights: string[];
  description: string;
  videoId: string;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(price);
}

function formatEventDate(d: string | null | undefined, locale: string): string {
  if (!d) return '';
  const datePart = d.slice(0, 10);
  const [y, m, day] = datePart.split('-').map(Number);
  if (!y || !m || !day) return '';
  return new Date(y, m - 1, day).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

function ClubLogo({ club, index }: { club: { name: string; logo: string }; index: number }) {
  const [imgError, setImgError] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="flex flex-col items-center gap-2"
    >
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-white/5 border border-brand-lightBlue/30 flex items-center justify-center p-1.5 overflow-hidden">
        {imgError || !club.logo ? (
          <span className="text-white text-xs font-medium text-center px-1 leading-tight">
            {club.name}
          </span>
        ) : (
          <img
            src={club.logo}
            alt={club.name}
            className="w-full h-full object-contain"
            onError={() => setImgError(true)}
          />
        )}
      </div>
      <span className="text-white text-xs font-medium text-center max-w-[100px] leading-tight">
        {club.name}
      </span>
    </motion.div>
  );
}

export function Tryouts() {
  const { t, i18n } = useTranslation();
  const [selectedPast, setSelectedPast] = useState(0);
  const { data } = useLandingContent();
  const isSpanish = i18n.language === 'es';
  const locale = isSpanish ? 'es-CL' : 'en-US';
  const pick = (es: string, en: string) => (isSpanish ? es : en) || es || en;

  const apiUpcoming = data?.tryouts?.upcoming ?? [];
  // CMS often has only one seeded event; keep both active fallbacks until CMS has ≥2.
  const featuredList: FeaturedTryout[] =
    apiUpcoming.length >= 2
      ? apiUpcoming.map((apiUpcomingItem, index) => ({
          key: apiUpcomingItem.id || `api-${index}`,
          title: apiUpcomingItem.title,
          subtitle: apiUpcomingItem.subtitle ?? '',
          location: apiUpcomingItem.location ?? '',
          venue: apiUpcomingItem.venue ?? '',
          address: apiUpcomingItem.address ?? '',
          dateDisplay: formatEventDate(apiUpcomingItem.event_date, locale) || '',
          tagline: t('tryouts.tagline'),
          intro: pick(apiUpcomingItem.description_es ?? '', apiUpcomingItem.description_en ?? '') || t('tryouts.nextIntro'),
          spots: apiUpcomingItem.spots ?? 0,
          price: apiUpcomingItem.price ?? 0,
          clubs: apiUpcomingItem.clubs.map((c) => ({ name: c.name, logo: c.logo_url })),
          categories: apiUpcomingItem.categories.map((c) => ({
            name: pick(c.name_es, c.name_en),
            accreditation: c.accreditation,
            event: c.event_time,
          })),
          includes: (isSpanish ? apiUpcomingItem.includes_es : apiUpcomingItem.includes_en) ?? [],
          buyLink: apiUpcomingItem.buy_link ?? FALLBACK_UPCOMING_TRYOUTS[0].buyLink,
        }))
      : FALLBACK_UPCOMING_TRYOUTS.map((fb) => ({
          key: fb.id,
          title: fb.title,
          subtitle: fb.subtitle,
          location: fb.location,
          venue: fb.venue,
          address: fb.address,
          dateDisplay: t(fb.dateKey),
          tagline: t(fb.taglineKey),
          intro: t(fb.introKey),
          spots: fb.spots,
          price: fb.price,
          clubs: fb.clubs,
          categories: fb.categories.map((c) => ({
            name: t(c.nameKey),
            accreditation: c.accreditation,
            event: c.event,
          })),
          includes: fb.includeKeys.map((k) => t(k)),
          buyLink: fb.buyLink,
        }));

  const pastTryouts: PastTryout[] = data?.tryouts?.past?.length
    ? data.tryouts.past.map((p) => {
        const isTryoutX = /^X Tryout Nacional/i.test(p.title) && !/^XI\b/i.test(p.title);
        const xFallback = FALLBACK_PAST_TRYOUTS.find((f) => f.id === 10);
        return {
          key: p.id,
          title: p.title,
          dateDisplay: formatEventDate(p.event_date, locale) || (isTryoutX ? t('tryouts.past10Date') : ''),
          location: p.location ?? (isTryoutX ? xFallback?.location ?? '' : ''),
          participants: isTryoutX ? (xFallback?.participants ?? 150) : (p.participants ?? 0),
          selected: isTryoutX ? (xFallback?.selected ?? 51) : (p.selected ?? 0),
          clubs: p.clubs.map((c) => c.name),
          highlights: isTryoutX
            ? (xFallback?.highlightKeys.map((k) => t(k)) ?? [])
            : ((isSpanish ? p.highlights_es : p.highlights_en) ?? []),
          description: isTryoutX
            ? t('tryouts.past10Desc')
            : pick(p.description_es ?? '', p.description_en ?? ''),
          videoId: isTryoutX ? (xFallback?.videoId || p.video_id || '') : (p.video_id ?? ''),
        };
      })
    : FALLBACK_PAST_TRYOUTS.map((p) => ({
        key: String(p.id),
        title: t(p.titleKey),
        dateDisplay: t(p.dateKey),
        location: p.location,
        participants: p.participants,
        selected: p.selected,
        clubs: p.clubs,
        highlights: p.highlightKeys.map((k) => t(k)),
        description: t(p.descriptionKey),
        videoId: p.videoId,
      }));

  const activePast = pastTryouts[Math.min(selectedPast, Math.max(0, pastTryouts.length - 1))];

  return (
    <section id="tryouts" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-darkNavy via-brand-navy to-brand-darkNavy" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,255,170,0.05)_0%,transparent_60%)]" />
      
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,215,0,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.3) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
          >
            <Trophy className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow tracking-wider">{t('tryouts.title')}</span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            {t('tryouts.sectionTitle1')}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-yellow via-amber-400 to-brand-yellow">
              {t('tryouts.sectionTitle2')}
            </span>
          </h2>
          
          <p className="text-lg md:text-xl text-dark-300 max-w-3xl mx-auto">
            {t('tryouts.sectionDesc')}
          </p>
        </motion.div>

        {/* Featured Tryouts - Upcoming Events */}
        <div className="mb-20 space-y-10">
          {featuredList.map((featured, featuredIndex) => (
        <motion.div
          key={featured.key}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 + featuredIndex * 0.1 }}
        >
          <div className="relative bg-gradient-to-br from-brand-navy/90 via-brand-navy/80 to-brand-darkNavy/90 backdrop-blur-xl rounded-3xl border border-brand-yellow/20 overflow-hidden">
            {/* Glow effect */}
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-brand-yellow/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-fifa-teal/10 rounded-full blur-3xl" />
            
            <div className="relative p-6 md:p-10">
              {/* Badge */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/50 text-green-400 text-xs font-bold uppercase tracking-wider animate-pulse">
                  🔥 {t('tryouts.openRegistration')}
                </span>
                <span className="px-3 py-1.5 rounded-full bg-brand-yellow/20 border border-brand-yellow/50 text-brand-yellow text-xs font-bold uppercase tracking-wider">
                  {featured.spots > 0 ? `${featured.spots} ` : ''}{t('tryouts.limitedSpots')}
                </span>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Left - Info */}
                <div>
                  <h3 className="text-3xl md:text-4xl font-black text-white mb-2">
                    {featured.title}
                  </h3>
                  <p className="text-xl text-brand-yellow font-semibold mb-2">
                    {featured.subtitle}{featured.subtitle && featured.location ? ' – ' : ''}{featured.location}
                  </p>
                  <p className="text-dark-300 mb-3">{featured.tagline}</p>
                  <p className="text-dark-400 text-sm mb-6">{featured.intro}</p>

                  {/* Event details */}
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-brand-yellow/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-6 h-6 text-brand-yellow" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">{featured.dateDisplay}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-brand-yellow/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-brand-yellow" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">{featured.venue}</p>
                        <p className="text-dark-400">{featured.address}</p>
                      </div>
                    </div>
                  </div>

                  {/* Categories / Schedules */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {featured.categories.map((cat, i) => (
                      <div 
                        key={i}
                        className="p-4 rounded-xl bg-brand-darkNavy/50 border border-brand-lightBlue/20"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-fifa-teal" />
                          <span className="text-white font-bold">{cat.name}</span>
                        </div>
                        <p className="text-sm text-dark-400">
                          {t('tryouts.accreditationLabel')} <span className="text-white">{cat.accreditation}</span>
                        </p>
                        <p className="text-sm text-dark-400">
                          {t('tryouts.eventLabel')} <span className="text-white">{cat.event}</span>
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Includes */}
                  <div className="space-y-2">
                    {featured.includes.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-dark-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right - Clubs & CTA */}
                <div className="flex flex-col">
                  {/* Clubs - Logos */}
                  <div className="mb-8">
                    <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-brand-yellow" />
                      {t('tryouts.scoutsPresent')}
                    </h4>
                    <div className="flex flex-wrap items-center gap-4">
                      {featured.clubs.map((club, i) => (
                        <ClubLogo key={`${featured.key}-${club.name}`} club={club} index={i} />
                      ))}
                    </div>
                  </div>

                  {/* Price & CTA */}
                  <div className="mt-auto">
                    <div className="p-6 rounded-2xl bg-gradient-to-br from-brand-yellow/20 to-amber-500/10 border border-brand-yellow/30">
                      <div className="flex items-end justify-between mb-2">
                        <div>
                          <p className="text-dark-400 text-sm uppercase tracking-wider">{t('tryouts.registration')}</p>
                          <p className="text-4xl font-black text-brand-yellow">
                            {formatPrice(featured.price)}
                          </p>
                        </div>
                        <Ticket className="w-12 h-12 text-brand-yellow/50" />
                      </div>
                      <p className="text-dark-400 text-xs mb-4">{t('tryouts.priceNote')}</p>
                      
                      <a 
                        href={featured.buyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button 
                          variant="primary" 
                          size="lg" 
                          fullWidth
                          icon={<ArrowRight className="w-5 h-5" />}
                          iconPosition="right"
                        >
                          {t('tryouts.buyTicket')}
                        </Button>
                      </a>
                      
                      <p className="text-center text-xs text-dark-400 mt-3">
                        {t('tryouts.limitedSpotsNote')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
          ))}
        </div>

        {/* Past Editions */}
        {activePast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-2xl md:text-3xl font-black text-white mb-8 text-center">
              🏆 {t('tryouts.pastEditionsTitle')}
            </h3>

            {/* Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {pastTryouts.map((tryout, i) => (
                <button
                  key={tryout.key}
                  onClick={() => setSelectedPast(i)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300
                    ${selectedPast === i 
                      ? 'bg-brand-yellow text-brand-darkNavy' 
                      : 'bg-brand-navy/60 text-dark-300 hover:text-white hover:bg-brand-lightBlue/20 border border-brand-lightBlue/20'
                    }
                  `}
                >
                  {tryout.title.replace('Tryout', '').trim()}
                </button>
              ))}
            </div>

            {/* Selected Edition Details */}
            <motion.div
              key={selectedPast}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-brand-navy/60 backdrop-blur-xl rounded-2xl border border-brand-lightBlue/20 p-6 md:p-8"
            >
              <div className="grid md:grid-cols-2 gap-8">
                {/* Info */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-brand-yellow/20 flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-brand-yellow" />
                    </div>
                    <div>
                      <h4 className="text-xl font-black text-white">
                        {activePast.title}
                      </h4>
                      <p className="text-dark-400">
                        {activePast.location}{activePast.location && activePast.dateDisplay ? ' • ' : ''}{activePast.dateDisplay}
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 rounded-xl bg-brand-darkNavy/50 border border-brand-lightBlue/10">
                      <p className="text-3xl font-black text-brand-yellow">
                        {activePast.participants}+
                      </p>
                      <p className="text-dark-400 text-sm">{t('tryouts.participants')}</p>
                    </div>
                    {activePast.selected > 0 && (
                      <div className="p-4 rounded-xl bg-brand-darkNavy/50 border border-brand-lightBlue/10">
                        <p className="text-3xl font-black text-green-400">
                          {activePast.selected}
                        </p>
                        <p className="text-dark-400 text-sm">{t('tryouts.selected')}</p>
                      </div>
                    )}
                  </div>

                  {/* Highlights */}
                  <div className="space-y-2">
                    {activePast.highlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-brand-yellow" />
                        <span className="text-dark-300">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Clubs & Video */}
                <div>
                  <h5 className="text-white font-bold mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-brand-yellow" />
                    {t('tryouts.participatingClubs')}
                  </h5>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {activePast.clubs.map((club) => (
                      <span
                        key={club}
                        className="px-3 py-1.5 rounded-lg bg-brand-lightBlue/10 border border-brand-lightBlue/20 text-dark-300 text-sm"
                      >
                        {club}
                      </span>
                    ))}
                  </div>

                  {/* Description */}
                  <p className="text-dark-400 text-sm mb-4">
                    {activePast.description}
                  </p>

                  {/* YouTube Video Embed */}
                  {activePast.videoId && (
                    <div className="relative aspect-video rounded-xl overflow-hidden border border-brand-lightBlue/20 bg-brand-darkNavy/80">
                      <iframe
                        src={`https://www.youtube.com/embed/${activePast.videoId}?rel=0&modestbranding=1`}
                        title={activePast.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                      />
                      {/* Glow effect */}
                      <div className="absolute inset-0 pointer-events-none border border-brand-yellow/10 rounded-xl" />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* CTA Bottom */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <AnimatedBorder className="inline-block">
            <div className="px-8 py-6 bg-brand-navy/80">
              <p className="text-white font-bold text-lg mb-2">
                {t('tryouts.cantAttend')}
              </p>
              <p className="text-dark-400 mb-4">
                {t('tryouts.applyOnline')}
              </p>
              <Button variant="outline" icon={<ChevronRight className="w-4 h-4" />} iconPosition="right">
                {t('tryouts.downloadApp')}
              </Button>
            </div>
          </AnimatedBorder>
        </motion.div>
      </div>
    </section>
  );
}
